import { useEffect, useState } from "react";
import { Chip, Stack, Button, Typography, CircularProgress, Collapse } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Delete, ExpandLess } from "@material-ui/icons";
import useSettings from "hooks/useSettings";
import ListingsMatchRow from "../listingsMatch/ListingsMatchRow";
import { formatShortCurrency } from "utils/Haim/utils";
import { ContactTableAvatar } from "../contacts/table/rows/ContactTableAvatar";
import { preventBubbling } from "utils/listings/helper";
import { fetchListingById } from "utils/listings/fetchListings";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

type CalendarListingCardProps = {
  listing: ProcessedListingData;
};

export function CalendarListingCard({ listing }: CalendarListingCardProps): JSX.Element {
  const [listingCardOpen, setListingCardOpen] = useState(false);

  const [listingData, setListingData] = useState<null | ProcessedListingData>(null);
  const [listingDeleted, setListingDeleted] = useState(false);
  const [loadingListingData, setLoadingListingData] = useState(true);

  const { t: translate } = useTranslation();

  const { themeDirection } = useSettings();

  const listingId = listing.id ?? "";

  useEffect(() => {
    if (!listingId) {
      setLoadingListingData(false);
      return;
    }

    const fetchListingData = async (): Promise<void> => {
      setLoadingListingData(true);
      try {
        const hit = await fetchListingById(listingId);
        if (!hit) setListingDeleted(true);
        setListingData(hit);
      } catch (error) {
        console.error("Error fetching listing data:", error);
      } finally {
        setLoadingListingData(false);
      }
    };

    fetchListingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const toggleCustomerCardOpen = (): void => setListingCardOpen(!listingCardOpen);

  let parsedListing = {
    ...listing,
    objectID: listing.id
  };

  if (listingData) {
    parsedListing = {
      ...parsedListing,
      ...listingData
    };
  }

  const isRTL = themeDirection === "rtl";

  const { address, price, imageUrls } = parsedListing;

  const { street, neighborhood, city } = address;

  let addressPart: string[];

  if (street) addressPart = [street, city];
  else addressPart = [neighborhood, city];

  const parsedAddressPart = addressPart.filter(Boolean).join(", ");
  const parsedPricePart = `(${formatShortCurrency(price)})`;

  const fullLabel = `${parsedAddressPart} ${parsedPricePart}`;

  const listingImage = imageUrls?.[0] ?? null;

  return (
    <Stack gap={1} onSubmit={preventBubbling()}>
      <Button
        sx={{
          color: "text.primary",
          justifyContent: "flex-start",
          "&:hover": {
            backgroundColor: "rgba(145, 158, 171, 0.08)"
          }
        }}
        startIcon={
          loadingListingData ? (
            <CircularProgress size={18} />
          ) : (
            <ExpandLess
              sx={{
                transition: "transform 200ms",
                transform: listingCardOpen
                  ? "rotate(0deg)"
                  : isRTL
                  ? "rotate(-90deg)"
                  : "rotate(90deg)"
              }}
            />
          )
        }
        disabled={loadingListingData}
        onClick={toggleCustomerCardOpen}
      >
        <Stack
          sx={{ width: "100%" }}
          gap={1}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" gap={1} alignItems="center">
            <ContactTableAvatar
              sx={{ fontSize: 12 }}
              avatarSize={24}
              avatarUrl={listingImage}
              callerFullName={fullLabel}
            />
            <Typography variant="subtitle1">{fullLabel}</Typography>
          </Stack>
          {listingDeleted && (
            <Chip
              icon={<Delete />}
              size="small"
              color="error"
              label={translate("calendarPage.form.deleted")}
            />
          )}
        </Stack>
      </Button>
      {parsedListing && (
        <Collapse sx={{ width: "100%" }} in={listingCardOpen} timeout="auto" unmountOnExit>
          <ListingsMatchRow isInEventDialog listing={parsedListing} />
        </Collapse>
      )}
    </Stack>
  );
}
